// Builds the quote video on the user's phone: canvas frames -> H.264 (WebCodecs) + ready-made AAC music -> MP4.
import { Muxer, ArrayBufferTarget } from "mp4-muxer";
import { SIZES, layout, drawFrame, loadCardFonts } from "@/lib/quoteCard";

const FPS = 30;
const CODECS = ["avc1.640028", "avc1.4d0028", "avc1.42e028", "avc1.640032", "avc1.42e032"];

export const videoTiming = (text) => {
  const n = text.split(/\s+/).filter(Boolean).length;
  const step = Math.min(0.45, 5.5 / n);
  const total = Math.min(10, Math.max(7, Math.ceil(0.6 + n * step + 2.8)));
  return { n, step, total };
};

// Every encoder setting this phone says it supports, full size first, then a smaller 720p fallback.
const configsFor = async (w, h) => {
  if (typeof window.VideoEncoder === "undefined" || typeof window.VideoFrame === "undefined") return [];
  const out = [];
  for (const scale of [1, 2 / 3]) {
    const width = Math.round((w * scale) / 2) * 2;
    const height = Math.round((h * scale) / 2) * 2;
    for (const codec of CODECS) {
      const cfg = { codec, width, height, bitrate: scale === 1 ? 4_000_000 : 2_500_000, framerate: FPS, avc: { format: "avc" } };
      try { if ((await VideoEncoder.isConfigSupported(cfg)).supported) out.push(cfg); } catch { /* try next */ }
    }
  }
  return out;
};

export const canMakeVideo = async () => (await configsFor(1080, 1920)).length > 0;

// ADTS AAC file -> raw AAC frames (1024 samples each).
const adtsFrames = (buf) => {
  const b = new Uint8Array(buf);
  const out = [];
  let i = 0;
  while (i + 7 <= b.length && b[i] === 0xff && (b[i + 1] & 0xf0) === 0xf0) {
    const head = b[i + 1] & 1 ? 7 : 9;
    const len = ((b[i + 3] & 3) << 11) | (b[i + 4] << 3) | (b[i + 5] >> 5);
    out.push(b.subarray(i + head, i + len));
    i += len;
  }
  return out;
};

const encodeWith = async (cfg, { text, author, handle, keyword, size, music }) => {
  const [w, h] = SIZES[size];
  const { n, step, total } = videoTiming(text);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  const L = layout(ctx, text, size, handle);
  // Smaller fallback sizes draw at full size, then scale down onto the output canvas.
  const out = cfg.width === w ? canvas : Object.assign(document.createElement("canvas"), { width: cfg.width, height: cfg.height });
  const octx = out === canvas ? null : out.getContext("2d");
  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: { codec: "avc", width: cfg.width, height: cfg.height, frameRate: FPS },
    ...(music ? { audio: { codec: "aac", numberOfChannels: 2, sampleRate: 44100 } } : {}),
    fastStart: "in-memory",
  });
  let failed = null;
  let chunks = 0;
  const enc = new VideoEncoder({ output: (c, m) => { chunks += 1; muxer.addVideoChunk(c, m); }, error: (e) => { failed = e; } });
  enc.configure(cfg);
  try {
    const frames = total * FPS;
    for (let f = 0; f < frames; f++) {
      if (failed) throw failed;
      const t = f / FPS;
      const shown = t < 0.6 ? 0 : Math.min(n, Math.floor((t - 0.6) / step) + 1);
      drawFrame(ctx, L, { author, handle, keyword }, shown, t >= 0.6 + n * step, 1 + 0.04 * (t / total));
      if (octx) octx.drawImage(canvas, 0, 0, cfg.width, cfg.height);
      const vf = new VideoFrame(out, { timestamp: Math.round((f * 1e6) / FPS), duration: Math.round(1e6 / FPS) });
      enc.encode(vf, { keyFrame: f % (FPS * 2) === 0 });
      vf.close();
      while (enc.encodeQueueSize > 4) await new Promise((r) => setTimeout(r, 5));
    }
    await enc.flush();
    if (failed) throw failed;
    if (!chunks) throw new Error("encoder produced nothing");
  } finally {
    if (enc.state !== "closed") enc.close();
  }
  if (music) {
    const meta = { decoderConfig: { codec: "mp4a.40.2", numberOfChannels: 2, sampleRate: 44100, description: new Uint8Array([0x12, 0x10]) } };
    const dur = (1024 / 44100) * 1e6;
    adtsFrames(music).forEach((fr, i) => {
      if ((i + 1) * dur <= total * 1e6) muxer.addAudioChunkRaw(fr, "key", Math.round(i * dur), Math.round(dur), i === 0 ? meta : undefined);
    });
  }
  muxer.finalize();
  return new Blob([muxer.target.buffer], { type: "video/mp4" });
};

// Phone encoders sometimes claim a setting works and then fail, so try each supported one in turn.
export const makeQuoteVideo = async (opts) => {
  await loadCardFonts();
  const [w, h] = SIZES[opts.size];
  const cfgs = await configsFor(w, h);
  if (!cfgs.length) throw new Error("unsupported");
  let last;
  for (const cfg of cfgs) {
    try { return await encodeWith(cfg, opts); } catch (e) { last = e; console.warn("video encoder setting failed", cfg.codec, cfg.width, e); }
  }
  throw last;
};
